import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Link2, Loader2, Plus, Search, ShieldOff, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const STATUS_VARIANTS = {
  active: "border-emerald-500/40 text-emerald-300",
  pending: "border-amber-500/40 text-amber-300",
  past_due: "border-rose-500/40 text-rose-300",
  cancelled: "border-zinc-600 text-zinc-400",
  expired: "border-zinc-700 text-zinc-500",
};

function AddMemberDialog({ tiers, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const initial = {
    tier_slug: tiers[0]?.slug || "plus",
    billing_cadence: "monthly",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address_line1: "",
    city: "Austin",
    state: "TX",
    zip_code: "",
    notes: "",
    source: "manual",
    status: "active",
  };
  const [form, setForm] = useState(initial);

  const submit = async () => {
    if (!form.customer_name || !form.customer_email) {
      toast.error("Name and email required");
      return;
    }
    setBusy(true);
    try {
      await crmApi.createMember(form);
      toast.success("Member added");
      setOpen(false);
      setForm(initial);
      onSaved?.();
    } catch (exc) {
      toast.error(exc.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-500 text-black hover:bg-amber-400" data-testid="add-member-btn">
          <UserPlus className="h-4 w-4 mr-1" /> Add member
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-black border-zinc-800 text-white max-w-xl">
        <DialogHeader>
          <DialogTitle>Add maintenance plan member</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Customer name</Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="member-name" placeholder="Jane Doe" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Email</Label>
            <Input value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="member-email" placeholder="customer@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Phone</Label>
            <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="member-phone" placeholder="512-555-1234" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Tier</Label>
            <Select value={form.tier_slug} onValueChange={(v) => setForm({ ...form, tier_slug: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="member-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                {tiers.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Billing cadence</Label>
            <Select value={form.billing_cadence} onValueChange={(v) => setForm({ ...form, billing_cadence: v })}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="member-cadence"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="annual">Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Address</Label>
            <Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" data-testid="member-address" placeholder="123 Maple Dr" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">ZIP</Label>
            <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-950 border-zinc-700 text-white" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} className="text-zinc-300">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="save-member-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Save member</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MagicLinkPanel({ member, onClose }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await crmApi.issueMagicLink(member.id);
        if (!active) return;
        const fullUrl = `${window.location.origin}${data.portal_path}`;
        setLink({ ...data, url: fullUrl });
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to issue link");
      } finally {
        if (active) setBusy(false);
      }
    })();
    return () => { active = false; };
  }, [member.id]);

  const reissue = async () => {
    setBusy(true);
    try {
      const data = await crmApi.issueMagicLink(member.id);
      const fullUrl = `${window.location.origin}${data.portal_path}`;
      setLink({ ...data, url: fullUrl });
      setCopied(false);
    } catch (exc) {
      toast.error(exc.message || "Failed to issue link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      toast.success("Copied portal link");
    } catch (_exc) {
      toast.error("Clipboard unavailable");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-black border-zinc-800 text-white">
        <DialogHeader><DialogTitle>Member portal link · {member.customer_name}</DialogTitle></DialogHeader>
        {busy && <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Issuing link…</div>}
        {link && (
          <div className="space-y-3" data-testid="magic-link-panel">
            <p className="text-xs text-zinc-400">
              Single-use link, expires {new Date(link.expires_at).toLocaleString()}. Phase D will auto-deliver via email/SMS.
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={link.url} className="bg-zinc-950 border-zinc-700 text-white font-mono text-xs" data-testid="magic-link-url" />
              <Button size="sm" onClick={copy} className="bg-amber-500 text-black hover:bg-amber-400" data-testid="copy-magic-link">
                <Copy className="h-3.5 w-3.5 mr-1" /> {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <Button size="sm" variant="outline" className="border-zinc-700 text-white" onClick={reissue} data-testid="reissue-magic-link">
              Re-issue link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MembersView() {
  const [members, setMembers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [magicLinkMember, setMagicLinkMember] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const [list, summary] = await Promise.all([crmApi.listMembers(params), crmApi.plansSummary()]);
      setMembers(list || []);
      setTiers(summary.tiers || []);
    } catch (exc) {
      toast.error(exc.message || "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    let active = true;
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (search) params.search = search;
    (async () => {
      try {
        const [list, summary] = await Promise.all([crmApi.listMembers(params), crmApi.plansSummary()]);
        if (!active) return;
        setMembers(list || []);
        setTiers(summary.tiers || []);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load members");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [search, statusFilter]);

  const cancelMember = async (member) => {
    if (!window.confirm(`Cancel ${member.customer_name}? Future dispatches will be cancelled.`)) return;
    try {
      await crmApi.updateMember(member.id, { status: "cancelled", cancel_reason: "Owner-initiated cancellation" });
      toast.success("Member cancelled");
      reload();
    } catch (exc) {
      toast.error(exc.message || "Cancel failed");
    }
  };

  const regenerate = async (member) => {
    try {
      const created = await crmApi.regenerateDispatches(member.id, 6);
      toast.success(`Scheduled ${created.length} pest dispatches`);
    } catch (exc) {
      toast.error(exc.message || "Schedule generation failed");
    }
  };

  const rows = useMemo(() => members, [members]);

  return (
    <div className="space-y-6" data-testid="owner-members">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Users className="h-5 w-5 text-amber-300" /> Members</h1>
          <p className="text-sm text-zinc-400 mt-1">Maintenance plan subscribers across all tiers.</p>
        </div>
        <AddMemberDialog tiers={tiers} onSaved={reload} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search name, email, phone, address" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500 w-72" data-testid="members-search" />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 bg-zinc-950 border-zinc-700 text-white" data-testid="members-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.keys(STATUS_VARIANTS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <Card className="bg-black/40 border-zinc-800">
          <CardContent className="py-10 text-center text-sm text-zinc-400">
            No members yet. Add one manually or wait for Stripe Checkout signups.
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-black/40">
          <table className="w-full text-sm" data-testid="members-table">
            <thead className="text-xs uppercase text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Tier</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-left py-2 px-3">Contact</th>
                <th className="text-left py-2 px-3">Signed up</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className="border-b border-zinc-900 hover:bg-zinc-900/30" data-testid={`member-row-${m.id}`}>
                  <td className="py-2 px-3">
                    <div className="text-white">{m.customer_name}</div>
                    <div className="text-[11px] text-zinc-500">{m.address_line1} {m.city}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-white">{m.tier_slug}</div>
                    <div className="text-[11px] text-zinc-500">{m.billing_cadence}</div>
                  </td>
                  <td className="py-2 px-3">
                    <Badge variant="outline" className={STATUS_VARIANTS[m.status] || "border-zinc-700 text-zinc-300"}>
                      {m.status}
                    </Badge>
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-zinc-300">{m.customer_email}</div>
                    <div className="text-[11px] text-zinc-500">{m.customer_phone || "—"}</div>
                  </td>
                  <td className="py-2 px-3 text-zinc-400">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="py-2 px-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setMagicLinkMember(m)} className="text-amber-300 hover:bg-amber-500/10" data-testid={`magic-link-btn-${m.id}`}>
                        <Link2 className="h-4 w-4" />
                      </Button>
                      {m.includes_pest_control && (
                        <Button size="sm" variant="ghost" onClick={() => regenerate(m)} className="text-zinc-300 hover:bg-zinc-800" data-testid={`regen-dispatch-${m.id}`} title="Add 6 more dispatches">
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                      {m.status !== "cancelled" && (
                        <Button size="sm" variant="ghost" onClick={() => cancelMember(m)} className="text-rose-300 hover:bg-rose-500/10" data-testid={`cancel-member-${m.id}`}>
                          <ShieldOff className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {magicLinkMember && (
        <MagicLinkPanel member={magicLinkMember} onClose={() => setMagicLinkMember(null)} />
      )}
    </div>
  );
}
